import { Component } from 'preact';
import { GOLD_BTN } from '../../lib/medievalTheme';

// Forged-plate button. The layered-depth recipe used by the in-game
// "End Turn" button, extracted so other prominent buttons share the
// same dimension: a concentric box-shadow stack (bright rim → dark
// groove → warm inner glow → outer halo → drop shadow), a doubled
// `tex-noise-panel` grit over the base fill, and engraved text.
//
// Props:
//   tone     'gold'. Primary action (bright gold, dark engraved text)
//            'bronze'. Secondary action (warm dark plate, gold text)
//   href     render an <a> (opens in a new tab) instead of a <button>
//   type     <button type>; defaults to "button" so a ForgedButton inside a
//            form never submits by accident. Pass "submit" for the one that should.
//   disabled stone-grey forged plate, no interaction
//
// Sizing/layout (padding, font, width, border-radius) come from the
// caller via className/style; this component only supplies the surface.

const NOISE = 'url("/tex-noise-panel.webp")';

const GOLD = {
  color: GOLD_BTN.color,
  blend: 'multiply, multiply, normal, overlay, overlay, overlay, overlay, normal, normal',
  bgIdle: [
    NOISE, NOISE,
    'linear-gradient(180deg, rgba(255,245,215,0.35) 0%, rgba(255,240,200,0.10) 30%, transparent 55%, rgba(60,35,10,0.22) 82%, rgba(60,35,10,0.42) 100%)',
    GOLD_BTN.background,
  ].join(', '),
  bgHover: [
    NOISE, NOISE,
    'linear-gradient(180deg, rgba(255,250,225,0.48) 0%, rgba(255,245,210,0.16) 30%, transparent 55%, rgba(95,55,12,0.22) 82%, rgba(95,55,12,0.40) 100%)',
    GOLD_BTN.background,
  ].join(', '),
  bgPress: [
    NOISE, NOISE,
    'linear-gradient(180deg, rgba(60,35,10,0.28) 0%, rgba(60,35,10,0.12) 35%, transparent 60%, rgba(255,240,200,0.10) 100%)',
    GOLD_BTN.background,
  ].join(', '),
  shadowIdle: 'inset 0 1px 0 rgba(255,240,200,0.45), inset 0 -1px 0 rgba(60,40,10,0.35), inset 0 0 22px rgba(120,80,20,0.22), 0 0 0 1px rgba(232,195,110,0.75), 0 0 0 3px rgba(0,0,0,0.55), 0 0 0 4px rgba(180,140,60,0.28), 0 6px 16px rgba(0,0,0,0.55)',
  shadowHover: 'inset 0 1px 0 rgba(255,245,215,0.6), inset 0 -1px 0 rgba(60,40,10,0.35), inset 0 0 26px rgba(160,110,35,0.32), 0 0 0 1px rgba(255,220,130,0.9), 0 0 0 3px rgba(0,0,0,0.55), 0 0 0 4px rgba(232,195,110,0.5), 0 8px 22px rgba(0,0,0,0.6), 0 0 26px rgba(232,195,110,0.28)',
  shadowPress: 'inset 0 2px 4px rgba(60,40,10,0.5), inset 0 -1px 0 rgba(255,240,200,0.18), inset 0 0 30px rgba(120,80,20,0.38), 0 0 0 1px rgba(212,168,67,0.7), 0 0 0 3px rgba(0,0,0,0.6), 0 0 0 4px rgba(180,140,60,0.22), 0 2px 6px rgba(0,0,0,0.55)',
  textShadow: '0 1px 0 rgba(255,240,200,0.5), 0 -1px 0 rgba(60,40,10,0.4)',
};

// Bronze uses a SOLID warm-dark base (opaque hex stops, no alpha) so
// it reads as solid forged bronze rather than translucent glass over
// the panel. Stone + noise textures are layered over the
// base (overlay/soft-light) for the same carved grit the gold buttons
// have. Depth comes from the shadow stack (shadows may keep alpha); the
// fill does not.
const BRONZE_BLEND = 'overlay, soft-light, normal';
const bronzeBg = (base) => [`url("/tex-noise.webp")`, `url("/tex-stone.webp")`, base].join(', ');

const BRONZE = {
  color: '#f0dca8',
  blend: BRONZE_BLEND,
  bgIdle: bronzeBg('linear-gradient(180deg, #574021 0%, #3b2a15 52%, #281c0e 100%)'),
  bgHover: bronzeBg('linear-gradient(180deg, #6b5029 0%, #49371b 52%, #332310 100%)'),
  bgPress: bronzeBg('linear-gradient(180deg, #3b2a15 0%, #281c0e 100%)'),
  shadowIdle: 'inset 0 1px 0 rgba(255,225,170,0.3), inset 0 -1px 0 rgba(40,25,8,0.4), inset 0 0 20px rgba(90,60,20,0.25), 0 0 0 1px rgba(212,168,67,0.55), 0 0 0 2px rgba(0,0,0,0.5), 0 0 0 3px rgba(150,110,50,0.22), 0 5px 14px rgba(0,0,0,0.5)',
  shadowHover: 'inset 0 1px 0 rgba(255,235,190,0.42), inset 0 -1px 0 rgba(40,25,8,0.4), inset 0 0 24px rgba(120,80,28,0.3), 0 0 0 1px rgba(232,195,110,0.8), 0 0 0 2px rgba(0,0,0,0.5), 0 0 0 3px rgba(180,140,60,0.4), 0 7px 18px rgba(0,0,0,0.55), 0 0 20px rgba(212,168,67,0.18)',
  shadowPress: 'inset 0 2px 4px rgba(40,25,8,0.5), inset 0 -1px 0 rgba(255,225,170,0.14), inset 0 0 26px rgba(90,60,20,0.34), 0 0 0 1px rgba(180,140,60,0.6), 0 0 0 2px rgba(0,0,0,0.55), 0 0 0 3px rgba(150,110,50,0.18), 0 2px 6px rgba(0,0,0,0.5)',
  textShadow: '0 1px 0 rgba(255,225,170,0.2), 0 -1px 0 rgba(0,0,0,0.5)',
};

const DISABLED = {
  color: 'rgba(160,152,142,0.85)',
  // Solid tarnished-iron fill; the radial is a light sheen (alpha ok).
  blend: 'normal, normal',
  bg: 'radial-gradient(ellipse 95% 65% at 50% 15%, rgba(210,205,195,0.18) 0%, transparent 62%), linear-gradient(180deg, #58544e 0%, #3a3732 50%, #262420 100%)',
  shadow: 'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.4), inset 0 0 24px rgba(0,0,0,0.35), 0 0 0 1px rgba(140,132,122,0.32), 0 0 0 3px rgba(0,0,0,0.45), 0 0 0 4px rgba(80,75,70,0.16), 0 4px 12px rgba(0,0,0,0.45)',
  textShadow: '0 1px 0 rgba(0,0,0,0.6)',
};

const TONES = { gold: GOLD, bronze: BRONZE };

export default class ForgedButton extends Component {
  baseStyle() {
    const { tone = 'gold', disabled, radius = '0.6cqw', style = {} } = this.props;
    const t = TONES[tone] || GOLD;
    if (disabled) {
      return {
        background: DISABLED.bg,
        backgroundBlendMode: DISABLED.blend,
        color: DISABLED.color,
        border: 'none',
        borderRadius: radius,
        boxShadow: DISABLED.shadow,
        textShadow: DISABLED.textShadow,
        ...style,
      };
    }
    return {
      background: t.bgIdle,
      backgroundBlendMode: t.blend,
      color: t.color,
      border: 'none',
      borderRadius: radius,
      boxShadow: t.shadowIdle,
      textShadow: t.textShadow,
      transition: 'transform 80ms ease-out, box-shadow 80ms ease-out, background 80ms ease-out',
      transform: 'scale(1)',
      textDecoration: 'none',
      ...style,
    };
  }

  // Hover/press feedback by swapping the background + shadow + scale, the
  // same way the End Turn button does (CSS pseudo-classes can't express
  // these multi-layer backgrounds reliably across both states).
  handlers() {
    const { tone = 'gold', disabled } = this.props;
    if (disabled) return {};
    const t = TONES[tone] || GOLD;
    const set = (el, bg, shadow, scale) => {
      el.style.background = bg;
      el.style.backgroundBlendMode = t.blend;
      el.style.boxShadow = shadow;
      el.style.transform = `scale(${scale})`;
    };
    return {
      onMouseEnter: (e) => set(e.currentTarget, t.bgHover, t.shadowHover, 1.015),
      onMouseLeave: (e) => set(e.currentTarget, t.bgIdle, t.shadowIdle, 1),
      onMouseDown: (e) => set(e.currentTarget, t.bgPress, t.shadowPress, 0.985),
      onMouseUp: (e) => set(e.currentTarget, t.bgHover, t.shadowHover, 1.015),
    };
  }

  render() {
    const { href, onClick, disabled, className = '', children, title, target, rel, type = 'button' } = this.props;
    const style = this.baseStyle();
    const handlers = this.handlers();
    if (href && !disabled) {
      return (
        <a
          href={href}
          target={target || '_blank'}
          rel={rel || 'noopener noreferrer'}
          className={className}
          style={style}
          title={title}
          {...handlers}
        >
          {children}
        </a>
      );
    }
    return (
      <button
        type={type}
        className={className}
        style={style}
        disabled={disabled}
        onClick={onClick}
        title={title}
        {...handlers}
      >
        {children}
      </button>
    );
  }
}
